import React from "react";
import type { GridRowId } from "@mui/x-data-grid-pro";
import i18n from "i18next";
import type { AgentDataGridContext } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import type { JsonObject, JsonValue } from "src/components/views/ai/assistant/uiContext/jsonTypes.ts";
import { AgentUiLeafNode } from "src/components/views/ai/assistant/uiContext/AgentUiNode.tsx";
import { toJsonValue } from "src/components/views/ai/assistant/uiContext/jsonValueUtils.ts";

export interface AgentDataGridUiContextProps<RowData extends object> extends React.PropsWithChildren {
	agentContext?: AgentDataGridContext<RowData>;
	gridId: string;
	selectedRowIds: GridRowId[];
	onlySelectedRow?: RowData;
}

export function AgentDataGridUiContext<RowData extends object>({
	agentContext,
	children,
	gridId,
	onlySelectedRow,
	selectedRowIds,
}: AgentDataGridUiContextProps<RowData>) {
	return (
		<AgentUiLeafNode
			enabled={selectedRowIds.length > 0}
			node={{
				kind: "dataGrid",
				key: agentContext?.key,
				pathSegments: dataGridPathSegments(selectedRowIds, onlySelectedRow, agentContext),
				data: agentContext?.data,
				gridId,
				selectedRowKeys: selectedRowIds.length > 1 ? selectedRowKeys(selectedRowIds) : undefined,
				selectedRow: selectedRow(selectedRowIds, onlySelectedRow, agentContext?.rowFields ?? []),
			}}
		>
			{children}
		</AgentUiLeafNode>
	);
}

function dataGridPathSegments<RowData extends object>(
	selectedRowIds: GridRowId[],
	onlySelectedRow: RowData | undefined,
	agentContext: AgentDataGridContext<RowData> | undefined,
): string[] | undefined {
	if (selectedRowIds.length === 1 && onlySelectedRow !== undefined) {
		const segment = agentContext?.selectedRowPathSegment?.(onlySelectedRow)?.trim();
		return segment == null || segment === "" ? undefined : [segment];
	}
	if (selectedRowIds.length > 1) {
		const segment =
			agentContext?.selectedRowsPathSegment?.(selectedRowIds.length)?.trim() ??
			i18n.t("rows_selected_count", { count: selectedRowIds.length });
		return segment === "" ? undefined : [segment];
	}
	return undefined;
}

function selectedRowKeys(selectedRowIds: GridRowId[]): JsonValue[] {
	return selectedRowIds
		.map((rowId) => toJsonValue(rowId))
		.filter((rowId): rowId is JsonValue => rowId !== undefined);
}

function selectedRow<RowData extends object>(
	selectedRowIds: GridRowId[],
	onlySelectedRow: RowData | undefined,
	rowFields: ReadonlyArray<Extract<keyof RowData, string>>,
): JsonObject | undefined {
	if (selectedRowIds.length !== 1) return undefined;
	const result: JsonObject = {};
	if (onlySelectedRow !== undefined) {
		for (const field of rowFields) {
			const value = toJsonValue(onlySelectedRow[field]);
			if (value !== undefined) {
				result[field] = value;
			}
		}
	}
	return result;
}
